<template>
    <o-modal @close="closeForm" modal-class="show d-block">
        <template v-slot:header>
            <h5 class="modal-title">{{ isEdit ? 'Edit Marketing' : 'Create Marketing' }}</h5>
        </template>
        <template v-slot:body>
            <div class="form-group mb-3">
                <label for="name" class="form-label">Marketing Name <span class="text-danger">*</span></label>
                <input
                    type="text"
                    class="form-control"
                    id="name"
                    v-model="formData.name"
                    :class="{ 'is-invalid': errors.name }"
                    placeholder="Enter marketing name"
                />
                <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
            </div>

            <div class="form-group mb-3">
                <label for="description" class="form-label">Description</label>
                <textarea
                    class="form-control"
                    id="description"
                    v-model="formData.description"
                    rows="3"
                    placeholder="Enter description (optional)"
                ></textarea>
            </div>

            <div class="form-group mb-3">
                <label for="assigned_user" class="form-label">Assign To</label>
                <v-select
                    id="assigned_user"
                    v-model="selectedUser"
                    :options="userOptions"
                    :filterable="false"
                    @search="onSearchUser"
                    @open="onDropdownOpen"
                    @close="onDropdownClose"
                    @option:selected="onUserSelected"
                    placeholder="Search user by name or email..."
                    label="label"
                    :reduce="user => user.id"
                    :get-option-key="(option) => option.id"
                    :append-to-body="false"
                    :clearable="false"
                >
                    <template #list-footer>
                        <div 
                            v-show="hasMoreUsers || isLoadingUsers" 
                            ref="infiniteScrollTrigger"
                            class="vs-infinite-scroll-trigger"
                        >
                            <div v-if="isLoadingUsers" class="vs-loading-indicator">
                                <span class="spinner-border spinner-border-sm me-2"></span>
                                <span>Loading more users... ({{ userOptions.length - 1 }} loaded)</span>
                            </div>
                            <div v-else-if="hasMoreUsers" class="vs-scroll-hint">
                                Scroll to load more... ({{ userOptions.length - 1 }} of {{ totalUsers }} users)
                            </div>
                        </div>
                    </template>
                    <template #no-options>
                        <div class="vs-no-options">
                            {{ searchQuery ? 'No users found' : 'Type to search users...' }}
                        </div>
                    </template>
                </v-select>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
                <o-button variant="secondary" @click="closeForm">Cancel</o-button>
                <o-button variant="primary" @click="submit" :disabled="isSubmitting || hasSubmitted">
                    {{ isSubmitting ? 'Saving...' : 'Save' }}
                </o-button>
            </div>
        </template>
    </o-modal>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { OModal, OButton } from '@/components/OmnihControl'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
    name: 'MarketingForm',
    components: {
        OModal,
        OButton,
        vSelect
    },
    props: {
        editedItem: {
            type: Object,
            default: null
        }
    },
    emits: ['save', 'close'],
    setup(props, { emit }) {
        const store = useStore()

        const isEdit = computed(() => !!props.editedItem)
        const isSubmitting = ref(false)
        const hasSubmitted = ref(false) // Prevent double submission within the same modal session

        const formData = reactive({
            id: null,
            name: '',
            description: '',
            assigned_user_id: 'me'
        })

        const errors = reactive({
            name: ''
        })

        const userOptions = ref([])
        const selectedUser = ref(null)
        const currentUserId = computed(() => store.state.Auth?.currUser?.id || null)
        const currentUserInfo = computed(() => store.state.Auth?.currUser || {})
        const isLoadingUsers = ref(false)
        const hasMoreUsers = ref(true)
        const currentPage = ref(1)
        const searchQuery = ref('')
        const totalUsers = ref(0)
        const isInitialLoad = ref(true) // Track apakah sudah load initial data
        const infiniteScrollTrigger = ref(null)
        const scrollContainer = ref(null)
        let searchTimeout = null
        let observer = null
        let isObserverActive = false
        let lastScrollTop = 0
        let originalUserMeta = null // Store original meta untuk restore nanti
        let originalUserFilter = null // Store original filter untuk restore nanti

        // Initialize dengan option "Assign to Me"
        const initializeCurrentUserOption = () => {
            if (currentUserInfo.value) {
                const currentUserOption = {
                    id: currentUserId.value,
                    label: `${currentUserInfo.value.first_name || ''} ${currentUserInfo.value.last_name || ''} (Me)`.trim(),
                    email: currentUserInfo.value.email || '',
                    first_name: currentUserInfo.value.first_name || '',
                    last_name: currentUserInfo.value.last_name || ''
                }
                
                // Pastikan current user selalu ada di posisi pertama
                const existingIndex = userOptions.value.findIndex(u => u.id === currentUserId.value)
                if (existingIndex === -1) {
                    userOptions.value.unshift(currentUserOption)
                }
            }
        }

        onMounted(async () => {
            initializeCurrentUserOption()
            
            // Save original user meta and filter state
            originalUserMeta = { ...store.state.User.meta }
            originalUserFilter = { ...store.state.User.filter }
            
            if (props.editedItem) {
                formData.id = props.editedItem.id
                formData.name = props.editedItem.name || ''
                formData.description = props.editedItem.description || ''
                formData.assigned_user_id = props.editedItem.assigned_user_id || currentUserId.value
                
                // Load assigned user details untuk edit mode
                if (props.editedItem.assigned_user_id && props.editedItem.assigned_user_id !== currentUserId.value) {
                    await loadAssignedUser(props.editedItem.assigned_user_id)
                }
                
                // Set selectedUser dengan ID
                selectedUser.value = props.editedItem.assigned_user_id || currentUserId.value
            } else {
                formData.assigned_user_id = currentUserId.value
                selectedUser.value = currentUserId.value
            }
        })

        onUnmounted(() => {
            // Cleanup observer
            disconnectObserver()
            if (searchTimeout) {
                clearTimeout(searchTimeout)
            }
            
            // Restore original meta and filter state
            if (originalUserMeta) {
                store.commit('User/switch_page', { meta: originalUserMeta })
            }
            if (originalUserFilter) {
                store.commit('User/add_filter', { filter: originalUserFilter })
            }
            store.commit('User/clear_search')
        })

        const disconnectObserver = () => {
            if (observer) {
                observer.disconnect()
                observer = null
                isObserverActive = false
            }
        }

        // Setup Intersection Observer untuk auto load more saat scroll
        const setupInfiniteScroll = () => {
            // Jangan setup ulang jika sudah aktif
            if (isObserverActive) return
            
            nextTick(() => {
                disconnectObserver()
                
                const trigger = infiniteScrollTrigger.value
                if (!trigger) return
                
                // Get scroll container reference
                const dropdownEl = document.querySelector('.vs__dropdown-menu')
                if (dropdownEl) {
                    scrollContainer.value = dropdownEl
                }
                
                observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting && hasMoreUsers.value && !isLoadingUsers.value) {
                                // Save scroll position sebelum load
                                if (scrollContainer.value) {
                                    lastScrollTop = scrollContainer.value.scrollTop
                                }
                                loadMoreUsers()
                            }
                        })
                    },
                    {
                        root: scrollContainer.value, // Use dropdown menu sebagai root
                        rootMargin: '100px',
                        threshold: 0
                    }
                )

                observer.observe(trigger)
                isObserverActive = true
            })
        }

        // Watch perubahan selectedUser untuk update formData
        watch(selectedUser, (newVal) => {
            if (newVal) {
                formData.assigned_user_id = newVal
            }
        })

        // Watch perubahan name untuk clear error saat user mengetik
        watch(() => formData.name, () => {
            // Clear error setiap kali user mengetik, biarkan validasi ulang saat submit
            if (errors.name) {
                errors.name = ''
            }
        })

        // Load assigned user details jika edit mode
        async function loadAssignedUser(userId) {
            if (!userId || userId === currentUserId.value) return
            
            try {
                // Cek apakah user sudah ada di options
                const userExists = userOptions.value.find(u => u.id === userId)
                if (userExists) return
                
                // Fetch user details dari API
                const userData = await store.dispatch('User/read_single', userId)
                if (userData) {
                    const assignedUserOption = {
                        id: userData.id,
                        label: `${userData.first_name || ''} ${userData.last_name || ''} (${userData.email || ''})`.trim(),
                        email: userData.email,
                        first_name: userData.first_name,
                        last_name: userData.last_name
                    }
                    
                    // Tambahkan ke options di posisi kedua (setelah current user)
                    const existingIndex = userOptions.value.findIndex(u => u.id === userId)
                    if (existingIndex === -1) {
                        // Insert setelah current user
                        userOptions.value.splice(1, 0, assignedUserOption)
                    }
                }
            } catch (err) {
                console.error('Failed to load assigned user:', err)
            }
        }

        async function loadUsers(page = 1, search = '') {
            if (isLoadingUsers.value) return // Prevent multiple simultaneous loads
            
            try {
                isLoadingUsers.value = true
                const businessProfile = await store.dispatch('Auth/getBusinessProfile')
                const filter = {}
                
                if (businessProfile.corporate_id) {
                    filter.entity = `corporate,${businessProfile.corporate_id}`
                } else if (businessProfile.branch_id) {
                    filter.entity = `branch,${businessProfile.branch_id}`
                }
                
                // Filter by campaign module
                filter.role = 'marketing'
                
                console.log('[Debug] loadUsers - page:', page, 'search:', search, 'filter:', filter)
                
                // Set meta dengan limit kecil untuk lazy loading
                const meta = {
                    limit: 20,  // Load 20 users per page
                    page: page,
                    total: 1
                }
                
                const searchParams = search ? { name: search } : {}
                
                store.commit('User/add_filter', { filter })
                store.commit('User/switch_page', { meta })
                if (search) {
                    store.commit('User/set_search', { search: searchParams })
                } else {
                    store.commit('User/clear_search')
                }
                
                console.log('[Debug] Before API call - Vuex state:', {
                    filter: store.state.User.filter,
                    meta: store.state.User.meta,
                    search: store.state.User.search
                })
                
                await store.dispatch('User/read')
                
                const newUsers = store.state.User?.users || []
                const totalPages = store.state.User?.meta?.total || 1
                const totalCount = store.state.User?.meta?.total_count || 0
                
                console.log('[Debug] API Response:', {
                    usersCount: newUsers.length,
                    totalPages,
                    totalCount,
                    firstUser: newUsers[0],
                    allUsers: newUsers.map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}`, email: u.email, roles: u.roles, role: u.role }))
                })
                
                // Update total users count (excluding current user)
                if (totalCount > 0) {
                    totalUsers.value = totalCount - 1 // Exclude current user dari count
                } else {
                    // Fallback: estimate from pagination
                    totalUsers.value = Math.max(totalPages * 20 - 1, userOptions.value.length)
                }
                
                // Transform users untuk vue-select
                const transformedUsers = newUsers
                    .filter(user => user.id !== currentUserId.value) // Exclude current user karena sudah ada
                    .map((user, index) => {
                        // Calculate nomor urut berdasarkan page
                        const itemNumber = (page - 1) * 20 + index + 1
                        return {
                            id: user.id,
                            label: `${itemNumber}. ${user.first_name || ''} ${user.last_name || ''} (${user.email || ''})`.trim(),
                            email: user.email,
                            first_name: user.first_name,
                            last_name: user.last_name,
                            itemNumber: itemNumber
                        }
                    })
                
                if (page === 1) {
                    // Cek apakah ini search atau normal load
                    const isSearching = search && search.length >= 2
                    
                    if (isSearching) {
                        // Saat search, HANYA tampilkan current user + hasil search
                        userOptions.value = []
                        
                        // Tambahkan current user
                        initializeCurrentUserOption()
                        
                        // Tambahkan hasil search (yang bukan current user)
                        userOptions.value.push(...transformedUsers)
                        
                        console.log(`[Debug] Search "${search}": ${transformedUsers.length} users found`)
                    } else {
                        // Normal load (tanpa search)
                        // Pertahankan current user dan assigned user yang sudah di-fetch
                        const currentUser = userOptions.value.find(u => u.id === currentUserId.value)
                        const assignedUser = userOptions.value.find(u => u.id === formData.assigned_user_id && u.id !== currentUserId.value)
                        
                        userOptions.value = []
                        
                        // Rebuild dengan urutan: current user, assigned user (jika ada), kemudian data baru
                        if (currentUser) {
                            userOptions.value.push(currentUser)
                        } else {
                            initializeCurrentUserOption()
                        }
                        
                        if (assignedUser) {
                            userOptions.value.push(assignedUser)
                        }
                        
                        // Tambahkan data baru, skip yang sudah ada
                        const existingIds = new Set(userOptions.value.map(u => u.id))
                        const uniqueNewUsers = transformedUsers.filter(u => !existingIds.has(u.id))
                        userOptions.value.push(...uniqueNewUsers)
                        
                        console.log(`[Debug] Initial load: ${uniqueNewUsers.length} new users loaded. Total available: ${totalUsers.value}`)
                    }
                } else {
                    // Append untuk load more - preserve scroll position
                    const existingIds = new Set(userOptions.value.map(u => u.id))
                    const uniqueNewUsers = transformedUsers.filter(u => !existingIds.has(u.id))
                    
                    // Append data baru tanpa mengubah referensi array
                    if (uniqueNewUsers.length > 0) {
                        userOptions.value.push(...uniqueNewUsers)
                        
                        // Log untuk debug
                        console.log(`[Debug] Page ${page}: Appended ${uniqueNewUsers.length} users. Total loaded: ${userOptions.value.length - 1}/${totalUsers.value}`)
                        
                        // Preserve scroll position setelah append
                        await nextTick()
                        if (scrollContainer.value && lastScrollTop > 0) {
                            scrollContainer.value.scrollTop = lastScrollTop
                        }
                    }
                }
                
                hasMoreUsers.value = page < totalPages
                currentPage.value = page
                
                console.log(`[Debug] Page ${page} completed. HasMore: ${hasMoreUsers.value}, Current page: ${currentPage.value}/${totalPages}`)
                
                // Setup observer setelah data loaded (hanya untuk initial load dan dropdown open)
                if (page === 1) {
                    await nextTick()
                    setupInfiniteScroll()
                }
                
            } catch (err) {
                console.error('Failed to load users:', err)
            } finally {
                isLoadingUsers.value = false
            }
        }

        function onSearchUser(search, loading) {
            searchQuery.value = search
            
            if (search.length === 0) {
                // Reset ke initial state jika search kosong
                currentPage.value = 1
                hasMoreUsers.value = true
                disconnectObserver() // Disconnect observer saat reset
                
                // Reset isInitialLoad agar dropdown load ulang dari page 1
                isInitialLoad.value = true
                
                loadUsers(1, '')
                return
            }
            
            if (search.length < 2) {
                return
            }
            
            // Debounce search
            if (searchTimeout) clearTimeout(searchTimeout)
            
            loading(true)
            disconnectObserver() // Disconnect observer saat search baru
            
            searchTimeout = setTimeout(async () => {
                currentPage.value = 1
                hasMoreUsers.value = true
                
                // Saat search, mark sebagai not initial load
                // Ini akan membuat hasil search pure tanpa merge dengan data lama
                isInitialLoad.value = false
                
                await loadUsers(1, search)
                loading(false)
            }, 400) // Increase debounce untuk lebih smooth
        }

        function loadMoreUsers() {
            if (!isLoadingUsers.value && hasMoreUsers.value) {
                loadUsers(currentPage.value + 1, searchQuery.value)
            }
        }

        function onDropdownOpen() {
            // Selalu load dari page 1 jika belum pernah load atau hanya ada assigned user
            // Ini memastikan user bisa scroll ke atas untuk lihat user lain
            if (isInitialLoad.value) {
                currentPage.value = 1
                hasMoreUsers.value = true
                searchQuery.value = ''
                
                // Reset userOptions tapi pertahankan current user dan assigned user
                const currentUser = userOptions.value.find(u => u.id === currentUserId.value)
                const assignedUser = userOptions.value.find(u => u.id === formData.assigned_user_id && u.id !== currentUserId.value)
                
                // Clear dan rebuild dengan current user + assigned user
                userOptions.value = []
                if (currentUser) {
                    userOptions.value.push(currentUser)
                }
                if (assignedUser) {
                    userOptions.value.push(assignedUser)
                }
                
                // Load page 1 dan set flag
                loadUsers(1, '').then(() => {
                    isInitialLoad.value = false
                })
            } else {
                // Setup observer jika data sudah ada tapi observer belum aktif
                nextTick(() => {
                    if (!isObserverActive && infiniteScrollTrigger.value) {
                        setupInfiniteScroll()
                    }
                })
            }
        }

        function onDropdownClose() {
            // Disconnect observer saat dropdown ditutup untuk save resources
            disconnectObserver()
        }

        function onUserSelected(option) {
            formData.assigned_user_id = option ? option.id : currentUserId.value
        }

        function validate() {
            errors.name = ''

            if (!formData.name || formData.name.trim() === '') {
                errors.name = 'Marketing name is required'
                return false
            }

            if (formData.name.trim().length > 255) {
                errors.name = 'Marketing name must not exceed 255 characters'
                return false
            }

            return true
        }

        async function submit() {
            // Hard guard to prevent multiple rapid clicks from triggering duplicate creates
            if (isSubmitting.value || hasSubmitted.value) return

            if (!validate()) return

            // Mark as submitted immediately to prevent subsequent clicks
            hasSubmitted.value = true
            isSubmitting.value = true

            const dataToSubmit = {
                ...formData,
                name: formData.name.trim(),
                description: formData.description.trim()
            }

            // Restore meta sebelum submit
            if (originalUserMeta) {
                store.commit('User/switch_page', { meta: originalUserMeta })
            }
            if (originalUserFilter) {
                store.commit('User/add_filter', { filter: originalUserFilter })
            }
            store.commit('User/clear_search')

            try {
                // Emit save event (synchronous). Parent should handle async save and close the modal.
                emit('save', dataToSubmit)
            } catch (error) {
                console.error('Save failed:', error)
            } finally {
                isSubmitting.value = false
            }
        }

        function closeForm() {
            // Restore original meta and filter state sebelum close
            if (originalUserMeta) {
                store.commit('User/switch_page', { meta: originalUserMeta })
            }
            if (originalUserFilter) {
                store.commit('User/add_filter', { filter: originalUserFilter })
            }
            store.commit('User/clear_search')
            // Reset submission lock when closing the form (allow future submits if reopened)
            hasSubmitted.value = false
            emit('close')
        }

        return {
            isEdit,
            isSubmitting,
            formData,
            errors,
            userOptions,
            selectedUser,
            currentUserId,
            isLoadingUsers,
            hasMoreUsers,
            searchQuery,
            totalUsers,
            infiniteScrollTrigger,
            onSearchUser,
            loadMoreUsers,
            onDropdownOpen,
            onDropdownClose,
            onUserSelected,
            submit,
            closeForm
        }
    }
}
</script>

<style scoped>
.form-label {
    font-weight: 500;
    color: #333;
}

.text-danger {
    color: #dc3545;
}

.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    display: block;
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

/* Vue-select custom styling */
:deep(.v-select) {
    font-family: inherit;
    width: 100%;
}

:deep(.vs__dropdown-toggle) {
    padding: 0.375rem 0.75rem;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    min-height: 38px;
    width: 100%;
}

:deep(.vs__search) {
    margin: 0;
    padding: 4px 0;
}

:deep(.vs__search:focus) {
    margin: 0;
}

:deep(.vs__selected) {
    margin: 2px 2px 2px 0;
    padding: 0;
    border: none;
}

:deep(.vs__dropdown-menu) {
    max-height: 300px;
    overflow-y: auto;
    scroll-behavior: smooth;
    overscroll-behavior: contain;
    /* Improve rendering performance */
    transform: translateZ(0);
    -webkit-overflow-scrolling: touch;
    /* Fix width inconsistency */
    width: 100%;
    box-sizing: border-box;
    /* Account for scrollbar */
    scrollbar-gutter: stable;
}

/* Custom scrollbar untuk tampilan yang lebih baik */
:deep(.vs__dropdown-menu::-webkit-scrollbar) {
    width: 8px;
}

:deep(.vs__dropdown-menu::-webkit-scrollbar-track) {
    background: #f1f1f1;
    border-radius: 4px;
}

:deep(.vs__dropdown-menu::-webkit-scrollbar-thumb) {
    background: #c1c1c1;
    border-radius: 4px;
}

:deep(.vs__dropdown-menu::-webkit-scrollbar-thumb:hover) {
    background: #a8a8a8;
}

:deep(.vs__dropdown-option) {
    padding: 8px 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 36px;
    display: flex;
    align-items: center;
    contain: layout;
    transition: background-color 0.15s ease;
    box-sizing: border-box;
}

:deep(.vs__dropdown-option--highlight) {
    background: #0d6efd;
    color: #fff;
}

:deep(.vs__dropdown-option--selected) {
    background: #e7f3ff;
    color: #0d6efd;
    font-weight: 500;
}

:deep(.vs__dropdown-option--selected.vs__dropdown-option--highlight) {
    background: #0d6efd;
    color: #fff;
}

/* Infinite scroll trigger - tidak menggunakan position sticky */
.vs-infinite-scroll-trigger {
    padding: 8px 12px;
    text-align: center;
    color: #6c757d;
    font-size: 0.875rem;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;
    user-select: none;
}

.vs-loading-indicator,
.vs-scroll-hint {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 4px 0;
}

.vs-no-options {
    padding: 12px;
    text-align: center;
    color: #6c757d;
    font-size: 0.875rem;
}

:deep(.vs__spinner) {
    border-left-color: rgba(0, 0, 0, 0.1);
}

/* Spinner animation untuk loading state */
.spinner-border {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    vertical-align: text-bottom;
    border: 0.15em solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spinner-border 0.75s linear infinite;
    will-change: transform;
}

.spinner-border-sm {
    width: 0.875rem;
    height: 0.875rem;
    border-width: 0.12em;
}

@keyframes spinner-border {
    to {
        transform: rotate(360deg);
    }
}

/* Modal width styling untuk desktop */
@media (min-width: 768px) {
    :deep(.modal-dialog) {
        min-width: 600px;
        max-width: 700px;
    }
}

.form-control:focus,
.form-select:focus,
:deep(.v-select.vs--open .vs__dropdown-toggle),
:deep(.v-select.vs--focus .vs__dropdown-toggle) {
    border-color: var(--primary-color, #F93E3E) !important;
    box-shadow: 0 0 0 0.25rem var(--secondary-color, #FEE9E9) !important;
}
</style>
